import type { NodeEditorController } from './nodeEditor.js';
import type { ConnectionStateController } from './connectionState.js';
import type { AppState } from '../state/appState.js';
import type { HistoryStore } from '../editor/historyState.js';

interface ShortcutDeps {
  appState: AppState;
  connectionState: ConnectionStateController;
  historyStore: Pick<HistoryStore, 'performUndo' | 'performRedo'>;
  nodeEditor: Pick<NodeEditorController, 'deleteNode' | 'refreshGraphView' | 'renderGraph'>;
  renderEditorNavigation: () => void;
  renderSceneTree: () => void;
  updateParams: () => void;
  scheduleRender: () => void;
  isNodeEditorShortcutActive: () => boolean;
  clearSnapHighlights: () => void;
  hasCutGesture: () => boolean;
  clearCutGesture: () => void;
}

export function initKeyboardShortcuts(deps: ShortcutDeps): void {
  window.addEventListener('keydown', e => {
    const graphShortcutsActive = deps.isNodeEditorShortcutActive();
    if (e.key === 'Escape' && deps.connectionState.pendingConn && graphShortcutsActive) {
      deps.clearSnapHighlights();
      if (!deps.connectionState.cancelPendingConn()) {
        deps.connectionState.clearPendingConn();
        deps.nodeEditor.refreshGraphView();
      }
      return;
    }
    if (e.key === 'Escape' && deps.hasCutGesture() && graphShortcutsActive) {
      deps.clearCutGesture();
      return;
    }

    const activeTag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
    const isEditable = ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeTag);
    if ((e.ctrlKey || e.metaKey) && !isEditable) {
      if (e.code === 'KeyZ' && !e.shiftKey) {
        e.preventDefault();
        if (deps.historyStore.performUndo()) {
          deps.nodeEditor.renderGraph();
          deps.updateParams();
          deps.renderSceneTree();
          deps.renderEditorNavigation();
          deps.scheduleRender();
        }
        return;
      }
      if (e.code === 'KeyY' || (e.code === 'KeyZ' && e.shiftKey)) {
        e.preventDefault();
        if (deps.historyStore.performRedo()) {
          deps.nodeEditor.renderGraph();
          deps.updateParams();
          deps.renderSceneTree();
          deps.renderEditorNavigation();
          deps.scheduleRender();
        }
        return;
      }
    }

    if ((e.key === 'Delete' || e.key === 'Backspace') && !isEditable && graphShortcutsActive) {
      const { selectedNode } = deps.appState;
      if (selectedNode) deps.nodeEditor.deleteNode(selectedNode);
    }
  });
}
